// Volume Profile: where the trading actually happened, drawn as horizontal
// bars on the chart's right edge. TradingView sells this behind a paid tier;
// ours is computed client-side from the bars already on screen.
//
// Truth rule: when the feed carries real volume, the bars are volume-at-price.
// When it does not (spot feeds often ship zeros), the profile falls back to
// TIME-at-price -- how many bars traded through each level -- and says so in
// its corner label. A profile that silently pretends time is volume would be
// the exact dishonesty this product exists to refuse.

const BIN_COUNT = 28;

class VolumeProfileRenderer {
  constructor(source) {
    this._source = source;
  }

  draw(target) {
    const source = this._source;
    if (!source._enabled || !source._chart || !source._series) return;
    const bars = source._bars;
    if (!bars || bars.length < 10) return;

    target.useBitmapCoordinateSpace((scope) => {
      const ctx = scope.context;
      const hRatio = scope.horizontalPixelRatio;
      const vRatio = scope.verticalPixelRatio;
      const width = scope.bitmapSize.width;

      const timeScale = source._chart.timeScale();
      const range = timeScale.getVisibleRange();
      if (!range) return;

      // Profile of the VISIBLE bars: zooming is the query language.
      let lo = Infinity, hi = -Infinity;
      const visible = [];
      for (const bar of bars) {
        if (bar.time < range.from || bar.time > range.to) continue;
        visible.push(bar);
        if (bar.low < lo) lo = bar.low;
        if (bar.high > hi) hi = bar.high;
      }
      if (visible.length < 10 || !(hi > lo)) return;

      const hasVolume = visible.some((b) => b.volume);
      const bins = new Array(BIN_COUNT).fill(0);
      const binSize = (hi - lo) / BIN_COUNT;
      for (const bar of visible) {
        // Spread the bar's weight across every bin its range crossed --
        // assigning it all to the close would spike phantom shelves.
        const from = Math.max(0, Math.floor((bar.low - lo) / binSize));
        const to = Math.min(BIN_COUNT - 1, Math.floor((bar.high - lo) / binSize));
        const weight = (hasVolume ? (bar.volume || 0) : 1) / (to - from + 1);
        for (let i = from; i <= to; i += 1) bins[i] += weight;
      }
      const maxBin = Math.max(...bins);
      if (!(maxBin > 0)) return;

      const maxBarWidth = width * 0.22;
      ctx.save();
      for (let i = 0; i < BIN_COUNT; i += 1) {
        if (!bins[i]) continue;
        const priceTop = lo + (i + 1) * binSize;
        const priceBottom = lo + i * binSize;
        let y1 = null, y2 = null;
        try {
          y1 = source._series.priceToCoordinate(priceTop);
          y2 = source._series.priceToCoordinate(priceBottom);
        } catch (e) { continue; }
        if (y1 === null || y2 === null) continue;
        const top = Math.min(y1, y2) * vRatio;
        const height = Math.max(1, Math.abs(y2 - y1) * vRatio - 1);
        const barWidth = (bins[i] / maxBin) * maxBarWidth;
        const poc = bins[i] === maxBin;
        ctx.fillStyle = poc
          ? "rgba(201, 162, 39, 0.30)"
          : "rgba(201, 162, 39, 0.13)";
        ctx.fillRect(width - barWidth, top, barWidth, height);
      }
      // The corner label states WHAT this profile measures.
      ctx.fillStyle = "rgba(138, 126, 104, 0.6)";
      ctx.font = `${Math.round(9 * vRatio)}px "Inter Tight", system-ui, sans-serif`;
      ctx.textAlign = "right";
      ctx.textBaseline = "bottom";
      ctx.fillText(hasVolume ? "VOL @ PRICE" : "TIME @ PRICE",
                   width - 4 * hRatio, scope.bitmapSize.height - 4 * vRatio);
      ctx.restore();
    });
  }
}

class VolumeProfilePaneView {
  constructor(source) {
    this._source = source;
    this._renderer = new VolumeProfileRenderer(source);
  }
  zOrder() { return "bottom"; }
  update() {}
  renderer() { return this._renderer; }
}

export class VolumeProfile {
  constructor() {
    this._enabled = false;
    this._chart = null;
    this._series = null;
    this._bars = [];
    this._requestUpdate = null;
    this._paneViews = [new VolumeProfilePaneView(this)];
  }

  attached({ chart, requestUpdate }) {
    this._chart = chart;
    this._requestUpdate = requestUpdate;
  }

  detached() {
    this._chart = null;
    this._requestUpdate = null;
  }

  paneViews() { return this._paneViews; }
  updateAllViews() { this._paneViews.forEach((v) => v.update()); }
  autoscaleInfo() { return null; }

  setSeries(series) { this._series = series; }
  setBars(bars) {
    this._bars = bars || [];
    if (this._requestUpdate) this._requestUpdate();
  }
  setEnabled(on) {
    this._enabled = !!on;
    if (this._requestUpdate) this._requestUpdate();
  }
}
